import { useEffect, useRef } from "react"
import { Grid, MenuItem, Stack } from "@mui/material"
import { useFormikContext } from "formik"
import { FormikDropdown, FormikTextField } from "../../../../../../shared/components/forms/Formik"
import { StandardMainPanel } from "../../../../../../shared/components/forms/StandardMainPanel"
import { StandardForm } from "../../../../../../shared/components/forms/StandardForm"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { EditBillReceiverFormValues } from "./EditBillReceiverForm"
import { BillReceiverEndpointType } from "../settingsTypes"
import { BillReceiversType } from "../../../../../../shared/components/billReceivers/billReceiverTypes"
import {
    CVR_LENGTH,
    EAN_LENGTH,
    MAX_EMAIL_LENGTH,
    MAX_NAME_LENGTH,
} from "../../../../../../constants/inputValidationDefaults"

export function EditBillReceiverFormContent() {
    const { values, setFieldValue } = useFormikContext<EditBillReceiverFormValues>()

    const isPublicInstitution = values.billType === BillReceiversType.PUBLIC_INSTITUTION
    const isPrivatePerson = values.billType === BillReceiversType.PRIVATE_PERSON
    const isPrivateCompany = values.billType === BillReceiversType.PRIVATE_COMPANY
    const showContactFields = true

    const prevBillTypeRef = useRef(values.billType)
    const prevEndpointTypeRef = useRef(values.endpointType)

    // Update endpoint type when bill type changes and clear related fields
    useEffect(() => {
        const prevBillType = prevBillTypeRef.current
        if (prevBillType !== values.billType) {
            // Clear endpoint value when bill type changes
            setFieldValue("endpointValue", "")

            // Clear vatnumber when switching to PRIVATE_PERSON
            if (isPrivatePerson) {
                setFieldValue("vatnumber", "")
            }

            prevBillTypeRef.current = values.billType
        }

        if (isPublicInstitution) {
            setFieldValue("endpointType", BillReceiverEndpointType.EAN)
        } else if (isPrivatePerson) {
            setFieldValue("endpointType", BillReceiverEndpointType.EMAIL)
        }
    }, [values.billType, setFieldValue, isPublicInstitution, isPrivatePerson, isPrivateCompany])

    // Clear endpoint value when endpoint type changes
    useEffect(() => {
        if (prevEndpointTypeRef.current !== values.endpointType) {
            setFieldValue("endpointValue", "")
            prevEndpointTypeRef.current = values.endpointType
        }
    }, [values.endpointType, setFieldValue])

    return (
        <StandardForm>
            <StandardMainPanel>
                <Stack spacing="1rem">
                    <LocalizedStrict id="bill-receivers-dropdown-type" attrs={{ label: true }}>
                        <FormikDropdown name="billType" label="Bill receiver type" fullWidth>
                            <MenuItem value={BillReceiversType.PRIVATE_PERSON}>
                                <LocalizedStrict id="bill-receivers-select-private-person">
                                    <span>Private person</span>
                                </LocalizedStrict>
                            </MenuItem>
                            <MenuItem value={BillReceiversType.PUBLIC_INSTITUTION}>
                                <LocalizedStrict id="bill-receivers-select-public-institution">
                                    <span>Public institution</span>
                                </LocalizedStrict>
                            </MenuItem>
                            <MenuItem value={BillReceiversType.PRIVATE_COMPANY}>
                                <LocalizedStrict id="bill-receivers-select-private-company">
                                    <span>Private company</span>
                                </LocalizedStrict>
                            </MenuItem>
                        </FormikDropdown>
                    </LocalizedStrict>

                    {isPrivateCompany && (
                        <LocalizedStrict id="bill-receivers-form-label-company-number" attrs={{ label: true }}>
                            <FormikTextField
                                name="vatnumber"
                                label="Company number"
                                fullWidth
                                inputProps={{ maxLength: CVR_LENGTH }}
                            />
                        </LocalizedStrict>
                    )}

                    <LocalizedStrict
                        id={
                            isPrivateCompany
                                ? "bill-receivers-form-label-company-name"
                                : isPublicInstitution
                                  ? "bill-receivers-form-label-institution-name"
                                  : "bill-receivers-form-label-name"
                        }
                        attrs={{ label: true }}
                    >
                        <FormikTextField
                            name="name"
                            label={
                                isPrivateCompany ? "Company name" : isPublicInstitution ? "Institution name" : "Name"
                            }
                            fullWidth
                            inputProps={{ maxLength: MAX_NAME_LENGTH }}
                        />
                    </LocalizedStrict>

                    <LocalizedStrict id="bill-receivers-form-label-country" attrs={{ label: true }}>
                        <FormikDropdown name="country" label="Country" fullWidth>
                            <MenuItem value="DK">
                                <LocalizedStrict id="bill-receivers-form-country-select-da">
                                    <>Denmark</>
                                </LocalizedStrict>
                            </MenuItem>
                        </FormikDropdown>
                    </LocalizedStrict>

                    <LocalizedStrict
                        id="bill-receivers-form-label-address-1"
                        attrs={{ label: true, placeholder: true }}
                    >
                        <FormikTextField
                            name="addressLine1"
                            label="Address line 1"
                            placeholder="Street and street number"
                            fullWidth
                        />
                    </LocalizedStrict>

                    <LocalizedStrict
                        id="bill-receivers-form-label-address-2"
                        attrs={{ label: true, placeholder: true }}
                    >
                        <FormikTextField
                            name="addressLine2"
                            label="Address line 2"
                            placeholder="Floor, apartment no., etc."
                            fullWidth
                        />
                    </LocalizedStrict>

                    <Grid container flexWrap="nowrap" gap="1.25rem">
                        <Grid item xs={4}>
                            <LocalizedStrict id="bill-receivers-form-label-postal-code" attrs={{ label: true }}>
                                <FormikTextField
                                    name="postalCode"
                                    label="Postal code"
                                    fullWidth
                                    inputProps={{ maxLength: 4 }}
                                />
                            </LocalizedStrict>
                        </Grid>
                        <Grid item xs={8}>
                            <LocalizedStrict id="bill-receivers-form-label-city" attrs={{ label: true }}>
                                <FormikTextField
                                    name="city"
                                    label="City"
                                    fullWidth
                                    inputProps={{ maxLength: MAX_NAME_LENGTH }}
                                />
                            </LocalizedStrict>
                        </Grid>
                    </Grid>
                </Stack>
            </StandardMainPanel>

            <LocalizedStrict id="user-settings-edit-bill-receiver-contact-panel" attrs={{ title: true }}>
                <StandardMainPanel title="Contact person">
                    <Stack spacing="1rem">
                        <LocalizedStrict id="bill-receivers-form-label-contact-person" attrs={{ label: true }}>
                            <FormikTextField
                                name="contactPerson"
                                label="Contact person"
                                fullWidth
                                inputProps={{ maxLength: MAX_NAME_LENGTH }}
                            />
                        </LocalizedStrict>

                        <Grid container flexWrap="nowrap" gap="1.25rem">
                            <Grid item xs={6}>
                                <LocalizedStrict id="bill-receivers-form-label-email" attrs={{ label: true }}>
                                    <FormikTextField
                                        name="email"
                                        label="Email"
                                        fullWidth
                                        inputProps={{ maxLength: MAX_EMAIL_LENGTH }}
                                    />
                                </LocalizedStrict>
                            </Grid>
                            <Grid item xs={6}>
                                <LocalizedStrict id="bill-receivers-form-label-telephone" attrs={{ label: true }}>
                                    <FormikTextField name="telephone" label="Telephone" fullWidth />
                                </LocalizedStrict>
                            </Grid>
                        </Grid>
                    </Stack>
                </StandardMainPanel>
            </LocalizedStrict>

            <LocalizedStrict id="user-settings-edit-bill-receiver-endpoint-panel" attrs={{ title: true }}>
                <StandardMainPanel title="Invoice delivery">
                    {isPrivateCompany && (
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <LocalizedStrict
                                    id="user-settings-edit-bill-receiver-endpoint-type-field"
                                    attrs={{ label: true }}
                                >
                                    <FormikDropdown name="endpointType" label="Type" fullWidth>
                                        <MenuItem value={BillReceiverEndpointType.EMAIL}>Email</MenuItem>
                                        <MenuItem value={BillReceiverEndpointType.EAN}>EAN number</MenuItem>
                                    </FormikDropdown>
                                </LocalizedStrict>
                            </Grid>
                            <Grid item xs={8}>
                                <FormikTextField
                                    name="endpointValue"
                                    label={
                                        values.endpointType === BillReceiverEndpointType.EAN ? "EAN Number" : "Email"
                                    }
                                    fullWidth
                                    inputProps={{
                                        maxLength:
                                            values.endpointType === BillReceiverEndpointType.EAN
                                                ? EAN_LENGTH
                                                : MAX_EMAIL_LENGTH,
                                    }}
                                />
                            </Grid>
                        </Grid>
                    )}

                    {isPublicInstitution && (
                        <LocalizedStrict id="bill-receivers-input-search-label-ean-number" attrs={{ label: true }}>
                            <FormikTextField
                                name="endpointValue"
                                label="EAN Number"
                                fullWidth
                                inputProps={{ maxLength: EAN_LENGTH }}
                            />
                        </LocalizedStrict>
                    )}

                    {isPrivatePerson && (
                        <FormikTextField
                            name="endpointValue"
                            label="Email"
                            fullWidth
                            inputProps={{ maxLength: MAX_EMAIL_LENGTH }}
                        />
                    )}
                </StandardMainPanel>
            </LocalizedStrict>
        </StandardForm>
    )
}
